import { useState } from 'react';
import { useProviderAuth } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Building2, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export function ProviderAuthForm() {
  const { login, register } = useProviderAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Show/hide password
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [businessName, setBusinessName] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(loginEmail, loginPassword);
      toast.success('Accesso effettuato!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (regPassword !== regConfirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (regPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(regEmail, regPassword, businessName);
      toast.success('Registrazione completata! Benvenuto.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-umbria-paper to-umbria-stone/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0 bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-umbria-olive/10 rounded-2xl flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-umbria-olive" />
          </div>
          <CardTitle className="text-2xl font-serif text-umbria-charcoal">
            Portale Fornitori
          </CardTitle>
          <CardDescription className="text-umbria-muted">
            Gestisci la tua presenza su Feel Umbria
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Accedi</TabsTrigger>
              <TabsTrigger value="register">Registrati</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="la-tua@email.it"
                      className="pl-10"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="La tua password"
                      className="pl-10 pr-10"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-3 text-umbria-muted hover:text-umbria-charcoal transition-colors"
                      tabIndex={-1}
                      aria-label={showLoginPassword ? "Nascondi password" : "Mostra password"}
                    >
                      {showLoginPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-umbria-olive hover:bg-umbria-olive-dark"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accesso...</>
                  ) : (
                    'Accedi'
                  )}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business-name">Nome Attività</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="business-name"
                      type="text"
                      placeholder="Es. Agriturismo Il Poggio"
                      className="pl-10"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      required
                      data-testid="register-business-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="la-tua@email.it"
                      className="pl-10"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="reg-password"
                      type={showRegPassword ? "text" : "password"}
                      placeholder="Minimo 8 caratteri"
                      className="pl-10 pr-10"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      minLength={8}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      className="absolute right-3 top-3 text-umbria-muted hover:text-umbria-charcoal transition-colors"
                      tabIndex={-1}
                      aria-label={showRegPassword ? "Nascondi password" : "Mostra password"}
                    >
                      {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-confirm-password">Conferma Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                    <Input
                      id="reg-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Ripeti la password"
                      className="pl-10 pr-10"
                      value={regConfirmPassword}
                      onChange={(e) => setRegConfirmPassword(e.target.value)}
                      required
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-umbria-muted hover:text-umbria-charcoal transition-colors"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Nascondi password" : "Mostra password"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-umbria-olive hover:bg-umbria-olive-dark"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registrazione...</>
                  ) : (
                    'Crea Account'
                  )}
                </Button>
                
                <p className="text-xs text-center text-umbria-muted mt-4">
                  Registrandoti, accetti di inserire solo informazioni veritiere sulla tua attività.
                  I contenuti saranno verificati prima della pubblicazione.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
